import type { Dayjs } from "dayjs";
import dayjs from "utils/dayjs-config";
import {
  pickerDayInProjectTimezone,
  storedDateInProjectTimezone,
  todayInProjectTimezone
} from "../project-calendar-date";
import {
  MaterialDateConfirmationMilestone,
  ProjectCalendarOffs
} from "../types";

/**
 * Mirrors entity-dateblock createDisabledDateFn for projected_date.
 * @see entity-dateblock/components/three-timeline-milestones/index.tsx
 */
export function createProjectedDateDisabledFn(
  prevMilestone: MaterialDateConfirmationMilestone | undefined,
  timezoneId: string
) {
  return (current: Dayjs) => {
    const currDateFromPicker = pickerDayInProjectTimezone(current, timezoneId)
      .hour(13)
      .minute(23)
      .second(24);
    const todayDate = todayInProjectTimezone(timezoneId)
      .hour(13)
      .minute(23)
      .second(23);
    const beforeToday = currDateFromPicker.isBefore(todayDate);
    const dependentDate =
      prevMilestone?.actualDate || prevMilestone?.projectedDate;
    const beforePrev = dependentDate
      ? currDateFromPicker.isBefore(
          storedDateInProjectTimezone(dependentDate, timezoneId)!
        )
      : false;

    return beforeToday || beforePrev;
  };
}

/**
 * Mirrors entity-dateblock isActualDateDisabled for actual_date.
 * @see entity-dateblock/components/three-timeline-milestones/index.tsx
 */
export function createActualDateDisabledFn(
  prevMilestone: MaterialDateConfirmationMilestone | undefined,
  timezoneId: string
) {
  return (current: Dayjs) => {
    const after = pickerDayInProjectTimezone(current, timezoneId).isAfter(
      todayInProjectTimezone(timezoneId)
    );
    if (!prevMilestone) return after;

    const currDateFromPicker = pickerDayInProjectTimezone(current, timezoneId)
      .hour(13)
      .minute(0)
      .second(0);
    const before = currDateFromPicker.isBefore(
      storedDateInProjectTimezone(prevMilestone.actualDate, timezoneId)!
    );

    return before || after;
  };
}

export function composeDisabledDateWithCalendarOffs(
  baseDisabledFn: (current: Dayjs) => boolean,
  calendarOffs?: ProjectCalendarOffs
): (current: Dayjs) => boolean {
  const holidays = calendarOffs?.holidays;
  const weekends = calendarOffs?.weekends;

  const holidayDaySet =
    holidays && holidays.length > 0
      ? new Set(
          holidays
            .map((holiday) =>
              holiday?.date ? dayjs.tz(holiday.date).format("YYYY-MM-DD") : null
            )
            .filter((dayKey): dayKey is string => !!dayKey)
        )
      : null;

  const weekendSet =
    weekends && weekends.length > 0 ? new Set<number>(weekends) : null;

  return (current: Dayjs) => {
    if (baseDisabledFn(current)) return true;
    if (holidayDaySet?.has(current.format("YYYY-MM-DD"))) return true;
    if (weekendSet?.has(current.day())) return true;
    return false;
  };
}
